import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import TestCaseApi from "../../api/TestCaseApi";

function TestCaseDetails() {
  const navigate = useNavigate();
  const { projectId } = useParams();

  const [testCases, setTestCases] = useState([]);
  const [loading, setLoading] = useState(true);

  const [actualResults, setActualResults] = useState({});

  const [showModal, setShowModal] = useState(false);
  const [editingTestCase, setEditingTestCase] = useState(null);

  /* =========================================
     FORM STATES
  ========================================= */

  const [title, setTitle] = useState("");

  const [description, setDescription] = useState("");

  const [steps, setSteps] = useState([""]);

  const [expectedResult, setExpectedResult] = useState("");

  const [actualResult, setActualResult] = useState("");
  /* =========================================
     FETCH TEST CASES
  ========================================= */

  const fetchTestCases = async () => {
    try {
      setLoading(true);

      const res = await TestCaseApi.getProjectTestCases(projectId);

      console.log("PROJECT TEST CASES RESPONSE:", res);

      const responseData = res?.data?.data || res?.data || [];
      if (Array.isArray(responseData)) {
        setTestCases(responseData);

        const results = {};

        responseData.forEach((testCase) => {
          results[testCase._id] = testCase?.actualResult || "";
        });

        setActualResults(results);
      } else if (Array.isArray(responseData?.testCases)) {
        setTestCases(responseData.testCases);

        const results = {};

        responseData.testCases.forEach((testCase) => {
          results[testCase._id] = testCase?.actualResult || "";
        });

        setActualResults(results);
      } else {
        setTestCases([]);
      }
    } catch (error) {
      console.error("FETCH TEST CASES ERROR:", error);

      setTestCases([]);

      toast.error(
        error?.response?.data?.message || "Failed to fetch test cases",
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================
     INITIAL LOAD
  ========================================= */

  useEffect(() => {
    if (projectId) {
      fetchTestCases();
    }
  }, [projectId]);

  /* =========================================
     RESET FORM
  ========================================= */

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setSteps([""]);
    setExpectedResult("");
    setActualResult("");
    setEditingTestCase(null);
  };

  /* =========================================
     OPEN CREATE MODAL
  ========================================= */

  const openCreateModal = () => {
    resetForm();

    setShowModal(true);
  };

  /* =========================================
     CLOSE MODAL
  ========================================= */

  const closeModal = () => {
    setShowModal(false);

    resetForm();
  };

  /* =========================================
     HANDLE STEP CHANGE
  ========================================= */

  const handleStepChange = (index, value) => {
    const updatedSteps = [...steps];

    updatedSteps[index] = value;

    setSteps(updatedSteps);
  };

  /* =========================================
     ADD STEP
  ========================================= */

  const addStep = () => {
    setSteps([...steps, ""]);
  };

  /* =========================================
     REMOVE STEP
  ========================================= */

  const removeStep = (index) => {
    if (steps.length === 1) {
      return;
    }

    const updatedSteps = steps.filter((_, stepIndex) => stepIndex !== index);

    setSteps(updatedSteps);
  };

  /* =========================================
     CREATE / UPDATE TEST CASE
  ========================================= */

  const handleCreateTestCase = async (event) => {
    event.preventDefault();

    if (!title.trim()) {
      toast.error("Test case title is required");

      return;
    }

    const filteredSteps = steps.filter((step) => step.trim() !== "");

    const payload = {
      projectId,

      title: title.trim(),

      description: description.trim(),

      steps: filteredSteps,

      expectedResult: expectedResult.trim(),

      actualResult: actualResult.trim(),
    };

    console.log("TEST CASE PAYLOAD:", payload);

    try {
      if (editingTestCase) {
        await TestCaseApi.update(editingTestCase._id, payload);

        toast.success("Test case updated successfully");
      } else {
        await TestCaseApi.create(payload);

        toast.success("Test case created successfully");
      }

      closeModal();

      fetchTestCases();
    } catch (error) {
      console.error("SAVE TEST CASE ERROR:", error);

      toast.error(error?.response?.data?.message || "Failed to save test case");
    }
  };

  /* =========================================
     EDIT TEST CASE
  ========================================= */

  const handleEdit = (testCase) => {
    setEditingTestCase(testCase);

    setTitle(testCase?.title || "");

    setDescription(testCase?.description || "");

    setSteps(testCase?.steps?.length > 0 ? testCase.steps : [""]);

    setExpectedResult(testCase?.expectedResult || "");

    setActualResult(testCase?.actualResult || "");

    setShowModal(true);
  };

  /* =========================================
     DELETE TEST CASE
  ========================================= */

  const handleDelete = async (testCaseId) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this test case?",
    );

    if (!confirmed) {
      return;
    }

    try {
      await TestCaseApi.delete(testCaseId);

      toast.success("Test case deleted successfully");

      fetchTestCases();
    } catch (error) {
      console.error("DELETE TEST CASE ERROR:", error);

      toast.error(
        error?.response?.data?.message || "Failed to delete test case",
      );
    }
  };

  /* =========================================
     UPDATE STATUS
  ========================================= */

  /* =========================================
   UPDATE STATUS
========================================= */

  const handleStatusChange = async (testCaseId, status) => {
    try {
      const actualResult = actualResults[testCaseId]?.trim() || "";

      /*
      Actual result is required when
      executing a test case.
    */

      if (["passed", "failed", "blocked"].includes(status) && !actualResult) {
        toast.error("Please enter Actual Result before updating the status");

        return;
      }

      await TestCaseApi.updateStatus(testCaseId, {
        status,
        actualResult,
      });

      toast.success(`Test case marked as ${status}`);

      fetchTestCases();
    } catch (error) {
      console.error("UPDATE STATUS ERROR:", error);

      toast.error(error?.response?.data?.message || "Failed to update status");
    }
  };

  /* =========================================
     STATUS CLASS
  ========================================= */

  const getStatusClass = (status) => {
    switch (status) {
      case "passed":
        return "bg-success";

      case "failed":
        return "bg-danger";

      case "blocked":
        return "bg-dark";

      case "pending":
      default:
        return "bg-warning text-dark";
    }
  };

  /* =========================================
     STATUS LABEL
  ========================================= */

  const getStatusLabel = (status) => {
    switch (status) {
      case "passed":
        return "Passed";

      case "failed":
        return "Failed";

      case "blocked":
        return "Blocked";

      case "pending":
      default:
        return "Pending";
    }
  };

  /* =========================================
     COUNTS
  ========================================= */

  const passedCount = testCases.filter(
    (item) => item.status === "passed",
  ).length;

  const failedCount = testCases.filter(
    (item) => item.status === "failed",
  ).length;

  const pendingCount = testCases.filter(
    (item) => item.status === "pending",
  ).length;

  const blockedCount = testCases.filter(
    (item) => item.status === "blocked",
  ).length;

  /* =========================================
     LOADING
  ========================================= */

  if (loading) {
    return (
      <div className="content">
        <div className="text-center py-5">
          <div
            className="
              spinner-border
              text-primary
            "
            role="status"
          >
            <span className="visually-hidden">Loading...</span>
          </div>

          <div className="mt-3">Loading Test Cases...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="content">
      {/* =====================================
          PAGE HEADER
      ====================================== */}

      <div
        className="
          d-flex
          justify-content-between
          align-items-center
          mb-4
        "
      >
        <div>
          <button
            type="button"
            className="
              btn
              btn-link
              text-decoration-none
              p-0
              mb-2
            "
            onClick={() => navigate("/test-cases")}
          >
            ← Back to Test Cases
          </button>

          <h3 className="fw-bold mb-1">Test Case Management</h3>

          <p className="text-muted mb-0">
            Create, manage and execute test cases for this project
          </p>
        </div>

        <div className="d-flex gap-2">
          <button
            type="button"
            className="
              btn
              btn-outline-primary
            "
            onClick={fetchTestCases}
          >
            ↻ Refresh
          </button>

          <button
            type="button"
            className="
              btn
              btn-primary
            "
            onClick={openCreateModal}
          >
            + Add Test Case
          </button>
        </div>
      </div>

      {/* =====================================
          SUMMARY CARDS
      ====================================== */}

      <div className="row mb-4">
        <div className="col-md-3 mb-3">
          <div
            className="
              card
              border-0
              shadow-sm
              h-100
            "
          >
            <div className="card-body">
              <small className="text-muted">Total Test Cases</small>

              <h3 className="fw-bold mb-0">{testCases.length}</h3>
            </div>
          </div>
        </div>

        <div className="col-md-3 mb-3">
          <div
            className="
              card
              border-0
              shadow-sm
              h-100
            "
          >
            <div className="card-body">
              <small className="text-muted">Passed</small>

              <h3
                className="
                  fw-bold
                  text-success
                  mb-0
                "
              >
                {passedCount}
              </h3>
            </div>
          </div>
        </div>

        <div className="col-md-3 mb-3">
          <div
            className="
              card
              border-0
              shadow-sm
              h-100
            "
          >
            <div className="card-body">
              <small className="text-muted">Failed</small>

              <h3
                className="
                  fw-bold
                  text-danger
                  mb-0
                "
              >
                {failedCount}
              </h3>
            </div>
          </div>
        </div>

        <div className="col-md-3 mb-3">
          <div
            className="
              card
              border-0
              shadow-sm
              h-100
            "
          >
            <div className="card-body">
              <small className="text-muted">Pending / Blocked</small>

              <h3
                className="
                  fw-bold
                  text-warning
                  mb-0
                "
              >
                {pendingCount + blockedCount}
              </h3>
            </div>
          </div>
        </div>
      </div>

      {/* =====================================
          TEST CASE TABLE
      ====================================== */}

      <div
        className="
          card
          border-0
          shadow-sm
        "
      >
        <div
          className="
            card-header
            bg-white
            py-3
          "
        >
          <h5 className="fw-bold mb-0">Test Cases</h5>
        </div>

        <div className="card-body p-0">
          {testCases.length === 0 ? (
            <div className="text-center py-5">
              <div
                style={{
                  fontSize: "55px",
                }}
              >
                📋
              </div>

              <h5 className="fw-bold mt-3">No Test Cases Found</h5>

              <p className="text-muted">
                Start by creating your first test case.
              </p>

              <button
                type="button"
                className="
                  btn
                  btn-primary
                "
                onClick={openCreateModal}
              >
                + Create First Test Case
              </button>
            </div>
          ) : (
            <div className="table-responsive">
              <table
                className="
                  table
                  table-hover
                  align-middle
                  mb-0
                "
              >
                <thead className="table-light">
                  <tr>
                    <th className="ps-4">#</th>

                    <th>Test Case</th>

                    <th>Expected Result</th>

                    <th>Actual Result</th>

                    <th>Status</th>

                    <th>Execute</th>

                    <th className="text-end pe-4">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {testCases.map((testCase, index) => (
                    <tr key={testCase?._id}>
                      <td className="ps-4">{index + 1}</td>

                      <td>
                        <div className="fw-bold">{testCase?.title}</div>

                        {testCase?.description && (
                          <small className="text-muted">
                            {testCase.description}
                          </small>
                        )}

                        {testCase?.steps?.length > 0 && (
                          <div className="mt-2">
                            <small className="fw-bold">Steps:</small>

                            <ol className="mb-0 ps-3">
                              {testCase.steps.map((step, stepIndex) => (
                                <li key={stepIndex}>
                                  <small>{step}</small>
                                </li>
                              ))}
                            </ol>
                          </div>
                        )}
                      </td>

                      <td>
                        <small>{testCase?.expectedResult || "-"}</small>
                      </td>

                      <td
                        style={{
                          minWidth: "250px",
                          maxWidth: "300px",
                        }}
                      >
                        <div
                          style={{
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            lineHeight: "1.5",
                          }}
                        >
                          {testCase?.actualResult ? (
                            testCase.actualResult
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </div>
                      </td>

                      <td>
                        <span
                          className={`badge px-3 py-2 ${getStatusClass(
                            testCase?.status,
                          )}`}
                        >
                          {getStatusLabel(testCase?.status)}
                        </span>
                      </td>

                      <td>
                        <select
                          className="
                              form-select
                              form-select-sm
                            "
                          value={testCase?.status || "pending"}
                          onChange={(event) =>
                            handleStatusChange(testCase._id, event.target.value)
                          }
                        >
                          <option value="pending">Pending</option>

                          <option value="passed">Passed</option>

                          <option value="failed">Failed</option>

                          <option value="blocked">Blocked</option>
                        </select>
                      </td>

                      <td className="text-end pe-4">
                        <div
                          className="
                              d-flex
                              justify-content-end
                              gap-2
                            "
                        >
                          <button
                            type="button"
                            className="
                                btn
                                btn-outline-primary
                                btn-sm
                              "
                            onClick={() => handleEdit(testCase)}
                          >
                            Edit
                          </button>

                          <button
                            type="button"
                            className="
                                btn
                                btn-outline-danger
                                btn-sm
                              "
                            onClick={() => handleDelete(testCase._id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* =====================================
          CREATE / EDIT MODAL
      ====================================== */}

      {showModal && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{
            backgroundColor: "rgba(0,0,0,0.5)",
          }}
        >
          <div
            className="
              modal-dialog
              modal-lg
              modal-dialog-scrollable
            "
          >
            <div className="modal-content testcases">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">
                  {editingTestCase ? "Edit Test Case" : "Create Test Case"}
                </h5>

                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModal}
                />
              </div>

              <form onSubmit={handleCreateTestCase}>
                <div className="modal-body">
                  {/* TITLE */}

                  <div className="mb-3">
                    <label
                      className="
                        form-label
                        fw-semibold
                      "
                    >
                      Test Case Title *
                    </label>

                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter test case title"
                      value={title}
                      onChange={(event) => setTitle(event.target.value)}
                      required
                    />
                  </div>

                  {/* DESCRIPTION */}

                  <div className="mb-3">
                    <label
                      className="
                        form-label
                        fw-semibold
                      "
                    >
                      Description
                    </label>

                    <textarea
                      className="form-control"
                      rows="3"
                      placeholder="Describe what needs to be tested"
                      value={description}
                      onChange={(event) => setDescription(event.target.value)}
                    />
                  </div>

                  {/* TEST STEPS */}

                  <div className="mb-3">
                    <div
                      className="
                        d-flex
                        justify-content-between
                        align-items-center
                        mb-2
                      "
                    >
                      <label
                        className="
                          form-label
                          fw-semibold
                          mb-0
                        "
                      >
                        Test Steps
                      </label>

                      <button
                        type="button"
                        className="
                          btn
                          btn-sm
                          btn-outline-primary
                        "
                        onClick={addStep}
                      >
                        + Add Step
                      </button>
                    </div>

                    {steps.map((step, index) => (
                      <div
                        className="
                            input-group
                            mb-2
                          "
                        key={index}
                      >
                        <span className="input-group-text">{index + 1}</span>

                        <input
                          type="text"
                          className="form-control"
                          placeholder={`Step ${index + 1}`}
                          value={step}
                          onChange={(event) =>
                            handleStepChange(index, event.target.value)
                          }
                        />

                        {steps.length > 1 && (
                          <button
                            type="button"
                            className="
                                btn
                                btn-outline-danger
                              "
                            onClick={() => removeStep(index)}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* EXPECTED RESULT */}

                  <div className="mb-3">
                    <label
                      className="
      form-label
      fw-semibold
    "
                    >
                      Expected Result
                    </label>

                    <textarea
                      className="form-control"
                      rows="3"
                      placeholder="What should happen?"
                      value={expectedResult}
                      onChange={(event) =>
                        setExpectedResult(event.target.value)
                      }
                    />
                  </div>

                  {/* ACTUAL RESULT */}

                  <div className="mb-3">
                    <label
                      className="
      form-label
      fw-semibold
    "
                    >
                      Actual Result
                    </label>

                    <textarea
                      className="form-control"
                      rows="3"
                      placeholder="What actually happened during testing?"
                      value={actualResult}
                      onChange={(event) => setActualResult(event.target.value)}
                    />
                  </div>

                  {/* STATUS YAHAN NAHI HAI */}
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="
                      btn
                      btn-light
                    "
                    onClick={closeModal}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="
                      btn
                      btn-primary
                    "
                  >
                    {editingTestCase ? "Update Test Case" : "Create Test Case"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TestCaseDetails;
